import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  errorToastAdminConfig,
  errorToastId,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useConvertCreditAgreementFullArtifactIdsLazyQueryGQLType } from 'packages/creditAgreements/requests/requests.gql.generated';
import { UploadJsonDropzone } from 'packages/creditAgreements/ui/creditAgreementWip/UploadJsonDropzone';

type UploadJsonModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  creditFacilityId: string;
  artifactDocumentId: string | null;
  setValues: (values: React.SetStateAction<any>) => void;
  currentCreditAgreementId: string;
  operationMode: CreditAgreement.OperationMode;
};
export const UploadJsonModal = ({
  isOpen,
  setIsOpen,
  setValues,
  creditFacilityId,
  currentCreditAgreementId,
  artifactDocumentId,
  operationMode,
}: UploadJsonModalProps) => {
  const [json, setJSON] = useState<{ [key: string]: any } | undefined>(
    undefined,
  );
  const toast = useToast();

  const [
    convertCreditAgreementFullArtifactIds,
  ] = useConvertCreditAgreementFullArtifactIdsLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      try {
        const convertedJson = JSON.parse(
          data.convertCreditAgreementFullArtifactIds.creditAgreement,
        );
        setValues(convertedJson);
      } catch {
        if (!toast.isActive(errorToastId)) {
          errorToastAdminConfig.title = `Error parsing converted id JSON.`;
          toast(errorToastAdminConfig);
        }
      }
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="5xl" width="2xl">
        <ModalHeader>Upload</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Text>Upload Credit Agreement Full Artifact JSON</Text>
          <UploadJsonDropzone
            setJSON={setJSON}
            currentCreditAgreementId={currentCreditAgreementId}
          />
          <Flex justifyContent="center" mt="8">
            <Button
              onClick={() => {
                const jsonCopy = _.cloneDeep(json);
                if (jsonCopy) {
                  jsonCopy.creditFacilityId = creditFacilityId;
                  // reset s3Path to prevent exposing incorrect CA PDF
                  jsonCopy.s3Path = '';

                  if (
                    artifactDocumentId &&
                    'borrowingBaseMechanics' in jsonCopy &&
                    'calcAgentMechanics' in jsonCopy.borrowingBaseMechanics &&
                    jsonCopy.borrowingBaseMechanics.calcAgentMechanics
                      ._artifactDocumentId_
                  ) {
                    jsonCopy.borrowingBaseMechanics.calcAgentMechanics._artifactDocumentId_ = artifactDocumentId;
                  }

                  if (operationMode === 'admin') {
                    convertCreditAgreementFullArtifactIds({
                      variables: {
                        creditAgreementFullArtifact: {
                          id: uuidv4(),
                          creditFacilityId,
                          creditAgreement: JSON.stringify(jsonCopy),
                        },
                      },
                    });
                  } else {
                    setValues(jsonCopy);
                  }
                }
                setIsOpen(false);
              }}
              aria-label="Done"
            >
              Done
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
